import {
  ExtranetCustomerListItemDto,
  ExtranetCustomerUserRolesDto,
} from '@pharmac-extranet/extranet-api'
import { useLocalStorage } from '@pharmac-extranet/shared'
import { useQuery } from '@tanstack/react-query'
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../../services/HttpService'
import { useAuthenticationContext } from '../authentication/useAuthentication'
import { CustomerContext, CustomerContextDto } from './CustomerContext'
import { getUserCustomersQuery } from './customership.queries'

export const EXTRANET_CUSTOMERSHIP_STORAGE_KEY = 'encustomership'

type CustomerShipLocalStorage = {
  customerId: number | undefined
}

export const useCustomerContext = () => useContext(CustomerContext)

export const CustomerProvider = ({ children }: PropsWithChildren) => {
  const { user, isPharmacCustomerService } = useAuthenticationContext()

  const [customer, setCustomer] = useState<ExtranetCustomerListItemDto | false | null>(null)
  const [customerRoles, setCustomerRoles] = useState<ExtranetCustomerUserRolesDto | undefined>()

  const { data: userCustomers, isLoading } = useQuery({
    ...getUserCustomersQuery(),
    enabled: !!user,
  })

  const [localStorage, setLocalStorage] = useLocalStorage<CustomerShipLocalStorage>(
    EXTRANET_CUSTOMERSHIP_STORAGE_KEY,
    { customerId: undefined }
  )

  const handleSelectCustomer = (
    params: Partial<ExtranetCustomerListItemDto> | false,
    forcedCustomer?: ExtranetCustomerListItemDto
  ) => {
    if (params === false) {
      return setCustomer(false)
    }

    if (!isPharmacCustomerService) {
      const foundCustomer = !!userCustomers?.length && userCustomers.find((a) => a.id === params.id)

      const customerValue = !!foundCustomer ? foundCustomer : false

      if (customerValue) {
        setCustomer(customerValue)
        setLocalStorage({ customerId: customerValue.id })
      }
    }

    if (isPharmacCustomerService && !!forcedCustomer) {
      setCustomer(forcedCustomer)
    }
  }

  useEffect(() => {
    if (!isLoading && userCustomers) {
      const storedValue = localStorage?.customerId

      if (storedValue && userCustomers.length > 0) {
        handleSelectCustomer({ id: storedValue })
      }

      if (userCustomers.length === 0) {
        setCustomer(false)
      }
    }
  }, [isLoading, userCustomers])

  useEffect(() => {
    if (customer && !isPharmacCustomerService) {
      api.customers.getMyCustomerUserRoles(customer.id).then((response) => {
        setCustomerRoles(response)
      })
    }

    if (isPharmacCustomerService) {
      api.customers.getAvailableRoles().then((response) => {
        setCustomerRoles({
          isAdmin: true,
          roles: response,
        })
      })
    }
  }, [customer])

  //const isAfterJoin = window.location.search && window.location.search.includes('ncsm=true')

  const showModal = customer === null && !!userCustomers?.length

  const context = useMemo((): CustomerContextDto => {
    return {
      userCustomers: userCustomers ?? [],
      customer: !!customer ? customer : undefined,
      roles: customerRoles,
      setCustomer: handleSelectCustomer,
      hasInitialized: !isLoading,
      showModal,
    }
  }, [customer, userCustomers, isLoading, customerRoles])

  console.log('CustomerContext :: DEBUG :: ', { customer, userCustomers, customerRoles })

  return <CustomerContext.Provider value={context}>{children}</CustomerContext.Provider>
}
